import Loading from "components/Loading";
import StaticPopup from "components/StaticPopup";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReactHtmlParser from "react-html-parser";
import rfqService from "services/rfqService";
import { hideContactDetails } from "actions/businessActions";
import VendorCategoriesView from "views/Search2.0/components/BusinessPages/VendorCategoriesView";
import InternalVendorProfileContent from "./InternalVendorProfileContent";
import TrustedCompaniesSection, { VerifiedAssociationIcon } from "./TrustedCompaniesSection";

const hasDisplayValue = (value) => {
  if (Array.isArray(value)) return value.some(hasDisplayValue);
  if (value == null) return false;
  return String(value).trim().length > 0;
};

const toDisplayText = (value) => {
  if (Array.isArray(value)) {
    return value
      .flatMap((item) => (Array.isArray(item) ? item : [item]))
      .filter(hasDisplayValue)
      .join(", ");
  }

  return hasDisplayValue(value) ? String(value).trim() : "";
};

const toDisplayList = (value) => {
  const values = Array.isArray(value) ? value : [value];

  return values
    .flatMap((item) => (Array.isArray(item) ? item : [item]))
    .map((item) => {
      if (typeof item === "string" || typeof item === "number") return String(item).trim();
      if (!item || typeof item !== "object") return "";

      return toDisplayText(
        item.clientName ||
        item.companyName ||
        item.businessName ||
        item.entityName ||
        item.projectName ||
        item.certificateName ||
        item.name ||
        item.title ||
        item.label,
      );
    })
    .filter(Boolean);
};

const uniqueList = (items) => [...new Set(items.filter(hasDisplayValue))];

const normalizeProjects = (values = {}) => {
  const projectSource =
    values?.projects ||
    values?.latestProjects ||
    values?.projectDetails ||
    values?.businessProjects ||
    values?.projectNames;

  if (Array.isArray(projectSource) && projectSource.length > 0) {
    return projectSource
      .filter(hasDisplayValue)
      .map((project) => {
        if (typeof project === "string") {
          return { projectName: project };
        }

        return {
          projectName: toDisplayText(
            project?.projectName || project?.name || project?.title,
          ),
          description: toDisplayText(
            project?.description ||
              project?.projectDescription ||
              project?.workDescription ||
              project?.workDone,
          ),
          location: toDisplayText(
            project?.location ||
              project?.projectLocation ||
              project?.address ||
              project?.city,
          ),
          clientName: toDisplayText(
            project?.clientName ||
              project?.client ||
              project?.customerName ||
              project?.companyName,
          ),
        };
      })
      .filter((project) => hasDisplayValue(project?.projectName));
  }

  return toDisplayList(values?.projectNames).map((projectName) => ({
    projectName,
  }));
};

const ListComponent = ({ title, items, truncate = true, className = "" }) => (
  <section className={`min-w-0 rounded-2xl border border-[#E8E6DD] bg-white/90 p-4 shadow-sm ${className}`}>
    <h3 className="mb-3 text-sm font-semibold text-[#1F2937]">{title}</h3>
    <ul className="grid grid-cols-1 gap-x-6 gap-y-1 list-disc pl-4 text-[#4B5563] sm:grid-cols-2">
      {items?.map((item, index) => (
        <li key={index} className="min-w-0">
          <p className={`${truncate ? "truncate" : "break-words"} text-sm`} title={item}>
            {item}
          </p>
        </li>
      ))}
    </ul>
  </section>
);

const ProjectDetail = ({ label, value }) => {
  if (!hasDisplayValue(value)) return null;

  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-[#667085]">
        {label}
      </p>
      <p className="mt-1 break-words text-sm leading-5">
        {value}
      </p>
    </div>
  );
};

const ProjectsList = ({ projects = [] }) => {
  const [openProjectIndexes, setOpenProjectIndexes] = useState({});

  const toggleProjectDetails = (index) => {
    setOpenProjectIndexes((currentIndexes) => ({
      ...currentIndexes,
      [index]: !currentIndexes[index],
    }));
  };

  return (
    <section className="min-w-0 rounded-2xl border border-[#E8E6DD] bg-white/90 p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-[#1F2937]">Latest Projects</h3>
        <span className="rounded-full bg-[#F4F4EF] px-2.5 py-1 text-[11px] font-semibold text-[#667085]">
          {projects.length}
        </span>
      </div>
      <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-2">
        {projects.map((project, index) => {
          const isOpen = Boolean(openProjectIndexes[index]);
          const hasDetails =
            hasDisplayValue(project?.description) ||
            hasDisplayValue(project?.location) ||
            hasDisplayValue(project?.clientName);

          return (
            <div
              key={`${project?.projectName || "project"}-${index}`}
              className="min-w-0 overflow-hidden rounded-xl border border-[#EFEFE8] bg-[#FCFCFA]"
            >
              <button
                type="button"
                className="flex w-full items-center gap-3 px-3 py-2 text-left"
                onClick={() => toggleProjectDetails(index)}
              >
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#185FA5] text-[10px] font-semibold text-white">
                  {index + 1}
                </span>
                <p className="min-w-0 flex-1 break-words text-sm font-semibold leading-6 text-[#374151]">
                  {project?.projectName}
                </p>
                {hasDetails && (
                  <p className="shrink-0 text-blue-800 text-xs">
                    {/* <path
                      fillRule="evenodd"
                      d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.17l3.71-3.94a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06Z"
                      clipRule="evenodd"
                    /> */}
                    {isOpen ? "Close" : "View Details"}
                  </p>
                )}
              </button>

              {hasDetails && isOpen && (
                <div className="space-y-3 border-t border-[#EFEFE8] bg-white px-3 py-3">
                  <ProjectDetail label="Client Name" value={project?.clientName} />
                  <ProjectDetail label="Location" value={project?.location} />
                  <ProjectDetail label="Work Done" value={project?.description} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

const ContactTable = ({ contacts = [] }) => (
  <section className="min-w-0 rounded-2xl border border-[#E8E6DD] bg-white/90 p-4 shadow-sm md:col-span-2">
    <h3 className="mb-3 text-sm font-semibold text-[#1F2937]">Contact</h3>
    <div className="overflow-x-auto rounded-xl border border-[#E4E7EC]">
      <table className="w-full min-w-[560px] border-collapse text-left text-sm">
        <thead className="bg-[#F8F9FB] text-[11px] font-semibold text-[#667085]">
          <tr>
            <th className="w-14 px-4 py-3">Sr#</th>
            <th className="px-4 py-3">Name</th>
            <th className="px-4 py-3">Contact</th>
            <th className="px-4 py-3">Email</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#E4E7EC] text-[#475467]">
          {contacts.map((contact, index) => (
            <tr key={`${contact.mobile}-${contact.email}-${index}`}>
              <td className="px-4 py-3 font-semibold text-[#667085]">{index + 1}</td>
              <td className="px-4 py-3 font-semibold text-[#1F2937]">{contact.name}</td>
              <td className="px-4 py-3">
                {contact.mobile ? (
                  <a className="hover:text-[#185FA5] hover:underline" href={`tel:${contact.mobile}`}>
                    {contact.mobile}
                  </a>
                ) : (
                  "--"
                )}
              </td>
              <td className="px-4 py-3">
                {contact.email ? (
                  <a className="break-all hover:text-[#185FA5] hover:underline" href={`mailto:${contact.email}`}>
                    {contact.email}
                  </a>
                ) : (
                  "--"
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </section>
);

const BusinessInfoDrawer = ({
  openBusinessInfoDrawer,
  values,
  handleOpenBusinessInfoDrawerClose,
  internalVendorData,
  onViewRfq,
  onViewOrderValue,
  showMarketplaceContacts = false,
}) => {
  const highlights = values?.hightlights;
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(false);
  const [viewMore, setViewMore] = useState(true);
  const [openContactInfo, setOpenContactInfo] = useState(false);
  const [openContactInfoLoading, setOpenContactInfoLoading] = useState(false);
  const [contactInfo, setContactInfo] = useState([]);
  const [verifiedAssociation, setVerifiedAssociation] = useState({ key: "", verified: false });
  const descriptionRef = useRef(null);

  const userProfile = auth.user;
  const userId = userProfile?.userId;
  const entityId = userProfile?.userEntity?.entityId;
  const canViewTrustedCompanies = userProfile?.userEntity?.features?.some(
    (feature) => feature.featureName === "SUPPLIER_TRUSTED_COMPANIES" && feature.hasFeature === true,
  );
  const marketplaceSupplierId = Array.isArray(values?.supplierId) ? values.supplierId[0] : values?.supplierId;
  const associationKey = `${userId}-${entityId}-${marketplaceSupplierId}`;
  const showVerifiedAssociation = canViewTrustedCompanies &&
    verifiedAssociation.key === associationKey && verifiedAssociation.verified;
  const businessName = toDisplayText(values?.businessName);
  const annualTurnOver = toDisplayText(values?.annualTurnOver);
  const businessWebsite = toDisplayText(values?.businessWebsite);
  const gstNumber = toDisplayText(values?.gstNumber);
  const addressLine1 = toDisplayText(values?.addresses_line1);
  const addressLine2 = toDisplayText(values?.addresses_line2);
  const addressCity = toDisplayText(values?.addresses_city);
  const addressState = toDisplayText(values?.addresses_state);
  const clients = toDisplayList(values?.clientNames);
  const projects = normalizeProjects(values);
  const isoCertificates = toDisplayList(values?.isoCertificates);
  const marketplaceContacts = [
    {
      name: businessName || "Business Contact",
      mobile: toDisplayText(
        values?.businessMobileNumber || values?.businessMobile || values?.contact,
      ),
      email: toDisplayText(values?.businessEmail || values?.businessEmailNumber),
    },
    ...(Array.isArray(values?.contactDetails) ? values.contactDetails : []),
    ...contactInfo,
  ]
    .map((contact, index) => {
      const contactName = toDisplayText(contact?.name || contact?.user?.name);

      return {
        name:
          contactName.toLowerCase() === "primary"
            ? businessName || "Business Contact"
            : contactName || `Contact ${index + 1}`,
        mobile: toDisplayText(
          contact?.mobile || contact?.phone || contact?.contact || contact?.user?.mobile,
        ),
        email: toDisplayText(contact?.email || contact?.user?.email),
      };
    })
    .filter((contact) => contact.mobile || contact.email)
    .filter(
      (contact, index, contacts) =>
        contacts.findIndex(
          (item) =>
            item.mobile.toLowerCase() === contact.mobile.toLowerCase() &&
            item.email.toLowerCase() === contact.email.toLowerCase(),
        ) === index,
    );
  const vendorCategories =
    Array.isArray(values?.vendorCategories) && values.vendorCategories.length > 0
      ? values.vendorCategories
      : Array.isArray(internalVendorData?.vendorCategories)
        ? internalVendorData.vendorCategories
        : [];
  const trustedCompaniesSection = canViewTrustedCompanies && entityId && userId && marketplaceSupplierId ? (
    <div className="min-w-0 empty:hidden md:col-span-2">
      <TrustedCompaniesSection
        key={associationKey}
        supplierId={marketplaceSupplierId}
        entityId={entityId}
        active={openBusinessInfoDrawer}
        associationKey={associationKey}
        onVerificationChange={setVerifiedAssociation}
      />
    </div>
  ) : null;
  const handleCloseContactInfoPopup = () => {
    setOpenContactInfo(false);
  };

  const handleClickOpenContactInfoPopup = async () => {
    const supplierDetails = {
      supplierId: values?.supplierId,
      contact: values?.contact,
      supplierUrl: values.url,
    };
    // console.log("this is dispatchg", supplierDetails);
    dispatch(hideContactDetails(userId, supplierDetails, entityId));
    const usersContactDataPromises = values?.owners?.map((userId) =>
      rfqService.getUserContacts({ userId }).catch((error) => {
        // Handle error for a specific user
        console.error("Error fetching user details:", error);
        // Return a placeholder object for the user with an error flag
        return { error: true, message: error.message };
      })
    );
    // Wait for all user data promises to resolve or reject
    const usersContactData = await Promise.all(usersContactDataPromises);

    // Extract user details from each usersContactData object
    const contacts = usersContactData.map((contact) => {
      if (contact.error) {
        // Handle error for a specific user
        console.error("Error fetching user details:", contact.message);
        return {}; // Return null or handle the error as needed
      }
      // Extract user details
      return {
        mobile: contact.user?.mobile,
        email: contact.user?.email,
        name: contact.user?.name,
      };
    });
    // console.log("this is a conts contacts", contacts);
    setContactInfo(
      contacts?.filter((contact) => Object.keys(contact).length !== 0)
    );
    setOpenContactInfo(true);
    setOpenContactInfoLoading(false);
  };

  const handleHeightOfDescription = () => {
    if (descriptionRef?.current) {
      // Check if description height exceeds 94px after component is mounted
      if (descriptionRef?.current?.scrollHeight > 99) {
        setExpanded(true);
        setViewMore(false);
      }
    }
  };

  const getInitials = (input) => {
    const normalizedInput = Array.isArray(input)
      ? input.filter(Boolean).join(" ")
      : input;
    const text = normalizedInput == null ? "" : String(normalizedInput);

    return (
      text
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .map((word) => word[0]?.toUpperCase())
        .join("")
        .slice(0, 2) || "NA"
    );
  };

  useEffect(() => {
    setExpanded(false);
    setOpenContactInfoLoading(false);
    handleHeightOfDescription();
  }, [values?.description]);

  const toggleExpanded = () => {
    setViewMore(!viewMore);
  };

  return (
    <div
      className={`fixed inset-0 z-[9999] flex justify-end bg-slate-900/35 backdrop-blur-[2px] transition-opacity duration-300 ${
        openBusinessInfoDrawer ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
      }`}
      onClick={handleOpenBusinessInfoDrawerClose}
    >
      <div
        className={`h-full w-full text-black transition-transform duration-500 transform z-[10000] md:w-[90vw] xl:w-[70vw] ${
          openBusinessInfoDrawer ? "translate-x-0" : "translate-x-full"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="relative h-full min-w-0 overflow-y-auto overflow-x-hidden bg-gradient-to-b from-[#FFF8F1] via-white to-[#F3F6FF] shadow-2xl ring-1 ring-black/5 z-[10001]"
          tabIndex="-1"
          aria-hidden="true"
        >
          <div className={`relative overflow-hidden bg-[#FFF8F1] px-4 pb-5 pt-4 sm:px-6 ${internalVendorData ? "" : "border-b border-[#E8E6DD]"}`}>
            <div className="absolute -right-12 -top-12 h-32 w-32 rounded-full bg-[#FDBA74]/25" />
            <div className="absolute bottom-3 right-16 h-14 w-36 rounded-t-full bg-[#185FA5]/10" />
            <button
              onClick={handleOpenBusinessInfoDrawerClose}
              type="button"
              className="absolute right-4 top-4 z-20 flex h-8 w-8 items-center justify-center rounded-full border border-[#E5E1D8] bg-white/90 text-[#4B5563] shadow-sm transition hover:bg-[#FFF0E4] hover:text-[#111827]"
              data-modal-toggle="defaultModal"
            >
              <svg
                aria-hidden="true"
                className="h-4 w-4"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                ></path>
              </svg>
              <span className="sr-only">Close drawer</span>
            </button>

            <div className="relative z-10 flex flex-col gap-4 pr-10 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex min-w-0 flex-1 items-start gap-3">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-[#FDBA74] bg-white text-base font-bold text-[#185FA5] shadow-sm">
                  {getInitials(businessName)}
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="break-words text-xl font-semibold leading-6 tracking-tight text-[#111827]">
                    {businessName}
                  </h3>
                  {highlights && (
                    <p className="mt-1 break-words text-xs leading-5 text-[#667085]">
                      {highlights}
                    </p>
                  )}
                  {showVerifiedAssociation && (
                    <p className="mt-2 flex items-center gap-1.5 text-xs font-semibold leading-5 text-[#079455]">
                      <VerifiedAssociationIcon className="h-4 w-4" />
                      Verified Client Work Experience
                    </p>
                  )}
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-2 sm:pt-1">
              {/* <button
                onClick={() => {
                  setOpenContactInfoLoading(true);
                  handleClickOpenContactInfoPopup();
                }}
                type="button"
                className="w-fit"
              >
                <div className="inline-flex h-9 cursor-pointer items-center rounded-full bg-[#185FA5] px-4 text-center text-white shadow-sm transition hover:bg-[#124D86]">
                  <p className="m-auto text-xs font-semibold">
                    {openContactInfoLoading ? (
                      <Loading />
                    ) : (
                      "Show Contact Info"
                    )}
                  </p>
                </div>
              </button> */}

              {/* <a href={"/company/" + values?.url} target="_blank">
                <div className="border-blue-700 border cursor-pointer hover:border-blue-500 hover:text-blue-500 text-center text-gray-600 text-sm font-bold h-[36px] w-full rounded flex items-center ">
                  <p className="m-auto font-bold text-xs">
                    View Detailed Profile
                  </p>
                </div>
              </a> */}
              </div>
            </div>
          </div>

          {internalVendorData && (
            <div>
              <InternalVendorProfileContent
                vendor={{
                  ...values,
                  ...internalVendorData,
                  vendorName:
                    internalVendorData?.vendorName ||
                    internalVendorData?.Company ||
                    businessName,
                  description:
                    internalVendorData?.description ||
                    values?.hightlights ||
                    internalVendorData?.businessHeadline,
                  vendorCategories:
                    Array.isArray(internalVendorData.vendorCategories) && internalVendorData.vendorCategories.length
                      ? internalVendorData.vendorCategories
                      : vendorCategories,
                }}
                onViewRfq={onViewRfq}
                onViewOrderValue={onViewOrderValue}
                isMarketplaceVendor
                trustedCompaniesSection={trustedCompaniesSection}
              />
            </div>
          )}

          {!internalVendorData && <div className="grid grid-cols-1 items-start gap-4 px-4 py-5 sm:px-6 md:grid-cols-2">
            {(values?.establishYear || annualTurnOver || businessWebsite || gstNumber) && (
              <section className="min-w-0 rounded-2xl border border-[#E8E6DD] bg-white/90 p-4 shadow-sm">
                <h3 className="mb-3 text-sm font-semibold text-[#1F2937]">Summary</h3>
                <div className="space-y-2 break-words text-[#4B5563]">
                  {values?.establishYear && (
                    <p className=" text-sm">
                      Age of business :{" "}
                      {new Date().getFullYear() - values?.establishYear > 0 &&
                        `${
                          new Date().getFullYear() - values?.establishYear
                        } Years`}
                    </p>
                  )}
                  {annualTurnOver && (
                    <p className="text-sm">
                      Turnover : {annualTurnOver}.
                    </p>
                  )}
                  {businessWebsite && (
                    <p className="text-sm">
                      Website :{" "}
                      <a
                        href={businessWebsite}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-700 italic cursor-pointer hover:underline"
                      >
                        {businessWebsite}
                      </a>
                    </p>
                  )}
                  {gstNumber && (
                    <p className="text-sm">GST : {gstNumber}.</p>
                  )}
                </div>
                {/* <p className=">b-1">Avg size of project : 2000 Units</p>
                  <p>80% repeat customers</p> */}
              </section>
            )}

            {(addressLine1 ||
              addressLine2 ||
              addressCity ||
              addressState) && (
              <section className="min-w-0 rounded-2xl border border-[#E8E6DD] bg-white/90 p-4 shadow-sm">
                <h3 className="mb-3 text-sm font-semibold text-[#1F2937]">Address</h3>
                <p className="break-words text-sm leading-6 text-[#4B5563]">
                  {uniqueList([addressLine1, addressLine2, addressCity, addressState]).join(", ")}
                </p>
              </section>
            )}

            {showMarketplaceContacts && marketplaceContacts.length > 0 && (
              <ContactTable contacts={marketplaceContacts} />
            )}

            {trustedCompaniesSection}

            <VendorCategoriesView
              className="my-0 min-w-0 md:col-span-2"
              showViewOnlyBadge={false}
              vendorCategories={vendorCategories}
              showMakes
              readOnly
            />
            {/* {values?.country?.length >= 1 ? (
            <ListComponent title="Region Served" items={[`All Over ${values?.country}`]} />
          ) : (
            (values?.selectedcities?.length >= 1 ||
              values?.state?.length >= 1) && (
              <div className="">
                <h3 className="text-xl font-bold mb-3">Region Served</h3>
                <div className="pl-1 flex flex-col gap-4">
                  {values?.state?.length >= 1 && (
                    <div>
                      <h5 className="font-semibold text-gray-600 mb-1">
                        States
                      </h5>
                      <ul className="grid grid-cols-2 gap-y-[10px] gap-x-6 list-disc pl-4 mb-1 dark:text-gray-400">
                        {values?.state?.map((item, index) => (
                          <li key={index}>
                            <p className="truncate text-sm" title={item}>
                              {"All Over " + item}
                            </p>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {values?.selectedcities?.length >= 1 && (
                    <div>
                      <h5 className="font-semibold text-gray-600 mb-1">
                        Cities
                      </h5>
                      <ul className="grid grid-cols-2 gap-y-[10px] gap-x-6 list-disc pl-4 mb-1 dark:text-gray-400">
                        {values?.selectedcities?.map((item, index) => (
                          <li key={index}>
                            <p className="truncate text-sm" title={item}>
                              {item}
                            </p>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )
          )} */}

            {values?.state?.length === 36 ? (
              // Show "All Over India" if all 36 states are selected
              <ListComponent title="Region Served" items={["All Over India"]} />
            ) : (
              // If less than 36 states or specific cities are selected, display them
              (values?.selectedcities?.length >= 1 ||
                values?.state?.length >= 1) && (
                <section className="min-w-0 rounded-2xl border border-[#E8E6DD] bg-white/90 p-4 shadow-sm">
                  <h3 className="mb-3 text-sm font-semibold text-[#1F2937]">Address</h3>
                  <div className="flex flex-col gap-4">
                    {values?.state?.length >= 1 && (
                      <div>
                        <h5 className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#667085]">
                          States
                        </h5>
                        <ul className="grid grid-cols-1 gap-x-6 gap-y-1 list-disc pl-4 text-[#4B5563] sm:grid-cols-2">
                          {values?.state?.map((item, index) => (
                            <li key={index}>
                              <p className="break-words text-sm" title={item}>
                                {"All Over " + item}
                              </p>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {values?.selectedcities?.length >= 1 && (
                      <div>
                        <h5 className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#667085]">
                          Cities
                        </h5>
                        <ul className="grid grid-cols-1 gap-x-6 gap-y-1 list-disc pl-4 text-[#4B5563] sm:grid-cols-2">
                          {values?.selectedcities?.map((item, index) => (
                            <li key={index}>
                              <p className="break-words text-sm" title={item}>
                                {item}
                              </p>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </section>
              )
            )}

           
             {clients.length > 0 && (
              <section className="min-w-0 rounded-2xl border border-[#E8E6DD] bg-white/90 p-4 shadow-sm">
                <h3 className="mb-3 text-sm font-semibold text-[#1F2937]">Clients</h3>
                <ul className="list-disc space-y-1 break-words pl-4 text-sm text-[#4B5563]">
                  {clients.map((client, index) => (
                    <li key={index}>{client}</li>
                  ))}
                </ul>
              </section>
            )}

            {isoCertificates.length > 0 && (
              <ListComponent
                title="Certifications"
                items={isoCertificates}
                truncate={false}
                className="md:col-span-2"
              />
            )}
            {projects.length > 0 && (
              <div className="min-w-0 md:col-span-2">
                <ProjectsList projects={projects} />
              </div>
            )}
            {/* Business description commented */}
            {/* <div>
            <h3 className="text-xl font-bold mb-3">Description</h3>
            <div
              ref={descriptionRef}
              className={`overflow-hidden ${viewMore ? "h-auto" : "h-[97px]"}`}
            >
              <p className="text-[#5C5C5C] text-sm font-normal overflow-y-auto">
                {ReactHtmlParser(values?.description)}
              </p>
            </div>
            {expanded && (
              <button
                className="text-blue-500 font-bold"
                onClick={() => toggleExpanded()}
              >
                {viewMore ? "Read less" : "Read more"}
              </button>
            )}
          </div> */}
          </div>}
        </div>
      </div>
      <div onClick={(e) => e.stopPropagation()}>
        <StaticPopup
          openModal={openContactInfo}
          handleClose={handleCloseContactInfoPopup}
          header={"Contact Info"}
          size={"sm"}
          zIndex={10050}
          headerLogo={
            <svg
            width="23"
            height="24"
            viewBox="0 0 23 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M17 2.25C17.4125 2.25 17.75 2.5875 17.75 3V21C17.75 21.4125 17.4125 21.75 17 21.75H3.5C3.0875 21.75 2.75 21.4125 2.75 21V3C2.75 2.5875 3.0875 2.25 3.5 2.25H17ZM3.5 0C1.84531 0 0.5 1.34531 0.5 3V21C0.5 22.6547 1.84531 24 3.5 24H17C18.6547 24 20 22.6547 20 21V3C20 1.34531 18.6547 0 17 0H3.5ZM10.25 12C11.0456 12 11.8087 11.6839 12.3713 11.1213C12.9339 10.5587 13.25 9.79565 13.25 9C13.25 8.20435 12.9339 7.44129 12.3713 6.87868C11.8087 6.31607 11.0456 6 10.25 6C9.45435 6 8.69129 6.31607 8.12868 6.87868C7.56607 7.44129 7.25 8.20435 7.25 9C7.25 9.79565 7.56607 10.5587 8.12868 11.1213C8.69129 11.6839 9.45435 12 10.25 12ZM8.75 13.5C6.67812 13.5 5 15.1781 5 17.25C5 17.6625 5.3375 18 5.75 18H14.75C15.1625 18 15.5 17.6625 15.5 17.25C15.5 15.1781 13.8219 13.5 11.75 13.5H8.75ZM23 3.75C23 3.3375 22.6625 3 22.25 3C21.8375 3 21.5 3.3375 21.5 3.75V6.75C21.5 7.1625 21.8375 7.5 22.25 7.5C22.6625 7.5 23 7.1625 23 6.75V3.75ZM22.25 9C21.8375 9 21.5 9.3375 21.5 9.75V12.75C21.5 13.1625 21.8375 13.5 22.25 13.5C22.6625 13.5 23 13.1625 23 12.75V9.75C23 9.3375 22.6625 9 22.25 9ZM23 15.75C23 15.3375 22.6625 15 22.25 15C21.8375 15 21.5 15.3375 21.5 15.75V18.75C21.5 19.1625 21.8375 19.5 22.25 19.5C22.6625 19.5 23 19.1625 23 18.75V15.75Z"
              fill="#5B6882"
            />
            </svg>
          }
        >
          <div>
            <div className="bg-[#F2F4F7] py-[6px] px-2 mb-2">
              <p className="font-bold text-[#3F4B64]">Business Contact Details</p>
            </div>
            <div className="flex gap-4 p-2 pt-0 items-center">
              <svg
              width="30"
              height="27"
              viewBox="0 0 30 27"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                x="1.25"
                y="6.25"
                width="27.5"
                height="19.5"
                rx="2.75"
                stroke="#3F4B64"
                stroke-width="2.5"
              />
              <path
                d="M4 6.25H26C27.5188 6.25 28.75 7.48122 28.75 9V15.75H1.25V9C1.25 7.48122 2.48122 6.25 4 6.25Z"
                stroke="#3F4B64"
                stroke-width="2.5"
              />
              <path
                d="M11 16H19V19C19 19.5523 18.5523 20 18 20H12C11.4477 20 11 19.5523 11 19V16Z"
                fill="#3F4B64"
              />
              <mask id="path-4-inside-1_101_6" fill="white">
                <path d="M8 2C8 0.89543 8.89543 0 10 0H20C21.1046 0 22 0.895431 22 2V7H8V2Z" />
              </mask>
              <path
                d="M8 2C8 0.89543 8.89543 0 10 0H20C21.1046 0 22 0.895431 22 2V7H8V2Z"
                stroke="#3F4B64"
                stroke-width="5"
                mask="url(#path-4-inside-1_101_6)"
              />
              </svg>

              <div className="flex flex-col gap-[2px] text-sm text-gray-500">
                <p className="font-semibold text-sm text-[#1C2436]">
                  {values?.businessName}
                </p>
                {values?.businessEmail && <p>{values?.businessEmail}</p>}
                {values?.businessMobileNumber && (
                  <p>{values?.businessMobileNumber}</p>
                )}
              </div>
            </div>
          </div>
          {contactInfo.length >= 1 && (
            <div>
              <div className="bg-[#F2F4F7] py-[6px] px-2 mb-2">
                <p className="font-bold text-[#3F4B64]">Team Contact Details</p>
              </div>
              {contactInfo?.map((contact) => (
                <div className="flex gap-4 p-2 pt-0 items-center">
                  <div className="h-9 w-9 rounded-full flex items-center justify-center bg-[#3F4B64] text-white font-bold text-sm">
                    {getInitials(contact?.name)}
                  </div>
                  <div className="flex flex-col gap-[2px] text-sm text-gray-500">
                    <p className="font-semibold text-sm text-[#1C2436]">
                      {contact?.name}
                    </p>
                    <p>{contact?.email}</p>
                    <p>{contact?.mobile}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </StaticPopup>
      </div>
    </div>
  );
};

export default BusinessInfoDrawer;
